import React, { useEffect } from "react";
import { Box, IconButton, Modal, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AudioBox from "../Inbox/Features/ChatBoxFeatures/AudioBox";
import { getTaskFileKindLabel } from "../../utils/taskFormFileKind";

/**
 * Full-screen overlay for task / task-template form attachments (image, video, audio).
 * Documents open in a new tab and do not use this modal.
 */
export default function TaskFormMediaPreviewModal({ open, file, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!file) return null;

  const kind = file.kind;
  const typeLabel = getTaskFileKindLabel(kind);
  const displayName = file.name || file.originalName || "File";

  const renderContent = () => {
    if (kind === "image") {
      return (
        <Box
          component="img"
          src={file.url}
          alt={displayName}
          sx={{
            maxWidth: "90vw",
            maxHeight: "calc(90vh - 88px)",
            objectFit: "contain",
            display: "block",
          }}
        />
      );
    }

    if (kind === "video") {
      return (
        <Box
          component="video"
          src={file.url}
          controls
          playsInline
          sx={{
            maxWidth: "90vw",
            maxHeight: "calc(90vh - 88px)",
            borderRadius: 1,
            display: "block",
          }}
        />
      );
    }

    if (kind === "audio") {
      return (
        <Box sx={{ width: "min(440px, 90vw)", px: { xs: 1, sm: 2 } }}>
          <AudioBox audioUrl={file.url} onDarkBackground />
        </Box>
      );
    }

    return null;
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100vw",
          height: "100vh",
          bgcolor: "rgba(0, 0, 0, 0.9)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            px: 2,
            py: 1.5,
            zIndex: 10,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" sx={{ color: "rgba(255,255,255,0.75)" }}>
              {typeLabel}
            </Typography>
            <Typography
              variant="body1"
              noWrap
              title={displayName}
              sx={{ color: "#fff", fontWeight: 600 }}
            >
              {displayName}
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close preview" sx={{ color: "#fff", flexShrink: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            flex: 1,
            px: 2,
            pt: 7,
            pb: 2,
            boxSizing: "border-box",
          }}
        >
          {renderContent()}
        </Box>
      </Box>
    </Modal>
  );
}
